import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import WhatsAppButton from '../components/WhatsAppButton';
import { AuthContext } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import api from '../utils/api';

const StayDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { formatPrice, language } = useLanguageCurrency();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Early checkout: same-day (time only) or by-days (date, approval flow)
  const [earlyCheckoutType, setEarlyCheckoutType] = useState('sameDay'); // 'sameDay' | 'byDays'
  const [earlyCheckoutTime, setEarlyCheckoutTime] = useState(''); // e.g. "10:00" for same day
  const [earlyCheckoutDate, setEarlyCheckoutDate] = useState('');
  const [earlyCheckoutChecking, setEarlyCheckoutChecking] = useState(false);
  const [earlyCheckoutAvailable, setEarlyCheckoutAvailable] = useState(null);
  const [earlyCheckoutSubmitting, setEarlyCheckoutSubmitting] = useState(false);
  const [earlyCheckoutSettings, setEarlyCheckoutSettings] = useState(null); // refund days etc.
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // Late checkout (time picker: HH:mm, default 12:00 = no late checkout)
  const [lateCheckoutTime, setLateCheckoutTime] = useState('12:00');
  const [lateCheckoutSubmitting, setLateCheckoutSubmitting] = useState(false);
  const [lateCheckoutAvailabilityError, setLateCheckoutAvailabilityError] = useState(null); // modal error when property booked
  const [showLateCheckoutConfirmModal, setShowLateCheckoutConfirmModal] = useState(false);
  const [lateCheckoutConfirmData, setLateCheckoutConfirmData] = useState(null);

  // Add days
  const [addDaysDate, setAddDaysDate] = useState('');
  const [addDaysChecking, setAddDaysChecking] = useState(false);
  const [addDaysAvailable, setAddDaysAvailable] = useState(null);
  const [addDaysSubmitting, setAddDaysSubmitting] = useState(false);

  // Cancellation (client cancels before check-in)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { redirectTo: `/client/booking/${id}/stay-details` } });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      api.get(`/api/bookings/${id}`),
      api.get('/api/bookings/early-checkout-settings').catch(() => ({ data: {} }))
    ]).then(([bookRes, settingsRes]) => {
        if (!cancelled && bookRes.data?.reservation) {
          setReservation(bookRes.data.reservation);
          const r = bookRes.data.reservation;
          if (r.requestedCheckOutTime) {
            const d = new Date(r.requestedCheckOutTime);
            const h = String(d.getHours()).padStart(2, '0');
            const m = String(d.getMinutes()).padStart(2, '0');
            setLateCheckoutTime(`${h}:${m}`);
          } else if (r.lateCheckOutHours != null && r.lateCheckOutHours > 0) {
            const h = 12 + Math.floor(r.lateCheckOutHours);
            const m = Math.round((r.lateCheckOutHours % 1) * 60);
            setLateCheckoutTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
          }
        }
        if (!cancelled && settingsRes.data) setEarlyCheckoutSettings(settingsRes.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || (language === 'ar' ? 'تعذر تحميل تفاصيل الحجز' : 'Failed to load booking details'));
          setReservation(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, user, navigate, language]);

  const canModifyStay = reservation && (reservation.status === 'Confirmed' || reservation.status === 'CheckedIn');
  const hasEarlyCheckout = !!(reservation?.earlyCheckout?.type && (reservation.earlyCheckout.status === 'completed' || reservation.earlyCheckout.status === 'approved'));
  const canCancelReservation = reservation && reservation.status === 'Confirmed'; // Before check-in only
  const currentCheckOut = reservation?.checkOutDate ? new Date(reservation.checkOutDate) : null;
  const currentCheckIn = reservation?.checkInDate ? new Date(reservation.checkInDate) : null;
  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  // Helper to format date as YYYY-MM-DD in local timezone (not UTC)
  const formatDateYMD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse a date string or Date object to local calendar date (YYYY-MM-DD at midnight local)
  // NEVER use slice(0,10) on ISO strings - "2024-02-07T22:00:00.000Z" is Feb 8 in Egypt, not Feb 7
  const parseToLocalDate = (dateInput) => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Calculate min/max dates for early checkout by days
  // Min: day after check-in (must stay at least 1 night) OR tomorrow (can't checkout in the past)
  // Max: day before original checkout (otherwise it's not "early")
  const getEarlyCheckoutMinDate = () => {
    if (!reservation?.checkInDate) return undefined;
    
    // Parse check-in date to local calendar date
    const checkInLocal = parseToLocalDate(reservation.checkInDate);
    if (!checkInLocal) return undefined;
    
    // Day after check-in (must stay at least 1 night)
    const dayAfterCheckIn = new Date(checkInLocal);
    dayAfterCheckIn.setDate(dayAfterCheckIn.getDate() + 1);
    
    // Tomorrow (can't checkout in the past)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    // Return the later of the two dates
    const minDate = dayAfterCheckIn > tomorrow ? dayAfterCheckIn : tomorrow;
    return formatDateYMD(minDate);
  };
  
  const getEarlyCheckoutMaxDate = () => {
    if (!reservation?.checkOutDate) return undefined;
    
    // Parse checkout date to local calendar date
    const checkOutLocal = parseToLocalDate(reservation.checkOutDate);
    if (!checkOutLocal) return undefined;
    
    // Day before checkout (last valid early checkout date)
    const dayBeforeCheckout = new Date(checkOutLocal);
    dayBeforeCheckout.setDate(dayBeforeCheckout.getDate() - 1);
    
    return formatDateYMD(dayBeforeCheckout);
  };

  const checkEarlyCheckoutAvailability = () => {
    if (!earlyCheckoutDate) return;
    setEarlyCheckoutChecking(true);
    setEarlyCheckoutAvailable(null);
    api.get(`/api/bookings/${id}/early-checkout-availability`, { params: { newCheckOutDate: earlyCheckoutDate } })
      .then((res) => {
        setEarlyCheckoutAvailable(res.data?.isAvailable ?? false);
        setMessage({ type: res.data?.isAvailable ? 'success' : 'error', text: res.data?.message || '' });
      })
      .catch((err) => {
        setEarlyCheckoutAvailable(false);
        setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'تعذر التحقق' : 'Could not check availability') });
      })
      .finally(() => setEarlyCheckoutChecking(false));
  };

  const submitEarlyCheckout = () => {
    if (earlyCheckoutType === 'sameDay') {
      if (!earlyCheckoutTime || !currentCheckOut) return;
      const [hours, minutes] = earlyCheckoutTime.split(':').map((n) => parseInt(n, 10) || 0);
      const d = new Date(currentCheckOut);
      d.setHours(hours, minutes, 0, 0);
      const requestedCheckOutTime = d.toISOString();
      setEarlyCheckoutSubmitting(true);
      setMessage({ type: '', text: '' });
      api.patch(`/api/bookings/${id}/request-early-checkout`, { type: 'sameDay', requestedCheckOutTime, requestedCheckOutTimeOfDay: earlyCheckoutTime })
        .then((res) => {
          setReservation(res.data?.reservation || reservation);
          setMessage({ type: 'success', text: language === 'ar' ? 'تم تسجيل الخروج المبكر في نفس اليوم. لا يُطبّق أي رسم.' : 'Same-day early checkout recorded. No fee applies.' });
          setEarlyCheckoutTime('');
        })
        .catch((err) => {
          setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'فشل تسجيل الخروج المبكر' : 'Failed to submit early checkout') });
        })
        .finally(() => setEarlyCheckoutSubmitting(false));
      return;
    }
    if (earlyCheckoutType === 'byDays' && !earlyCheckoutDate) return;
    
    // Calculate fee and refund before showing confirmation modal
    // Use parseToLocalDate to correctly handle timezone (ISO strings like "2024-02-07T22:00:00Z" are Feb 8 in Egypt)
    const originalCheckOut = parseToLocalDate(reservation.checkOutDate);
    const checkIn = parseToLocalDate(reservation.checkInDate);
    const newCheckOut = parseToLocalDate(earlyCheckoutDate);
    
    // Validate: new checkout must be after check-in and before original checkout
    if (newCheckOut <= checkIn) {
      setMessage({ type: 'error', text: language === 'ar' ? 'تاريخ الخروج الجديد يجب أن يكون بعد تاريخ الدخول' : 'New checkout date must be after check-in date' });
      return;
    }
    if (newCheckOut >= originalCheckOut) {
      setMessage({ type: 'error', text: language === 'ar' ? 'تاريخ الخروج الجديد يجب أن يكون قبل تاريخ الخروج الأصلي' : 'New checkout date must be before original checkout date' });
      return;
    }
    
    // Calculate cancelled nights (difference between original and new checkout)
    const cancelledNights = Math.round((originalCheckOut - newCheckOut) / (1000 * 60 * 60 * 24));
    const pricePerNight = reservation.pricing?.pricePerNight || 0;
    const valueOfCancelledDays = pricePerNight * cancelledNights;
    
    let feeAmount = 0;
    if (earlyCheckoutSettings?.feeType === 'percentage') {
      feeAmount = (valueOfCancelledDays * Math.min(100, Math.max(0, earlyCheckoutSettings.feeValue))) / 100;
    } else if (earlyCheckoutSettings?.feeType === 'fixed') {
      feeAmount = Math.min(valueOfCancelledDays, Math.max(0, earlyCheckoutSettings.feeValue || 0));
    }
    const refundAmount = Math.max(0, valueOfCancelledDays - feeAmount);
    
    setConfirmationData({
      cancelledNights,
      valueOfCancelledDays,
      feeAmount,
      refundAmount,
      newCheckOutDate: earlyCheckoutDate
    });
    setShowConfirmModal(true);
  };
  
  const confirmEarlyCheckout = () => {
    setEarlyCheckoutSubmitting(true);
    setMessage({ type: '', text: '' });
    setShowConfirmModal(false);
    
    api.patch(`/api/bookings/${id}/request-early-checkout`, { type: 'byDays', requestedCheckOutDate: confirmationData.newCheckOutDate })
      .then((res) => {
        setReservation(res.data?.reservation || reservation);
        const days = res.data?.refundProcessingDays || (earlyCheckoutSettings ? `${earlyCheckoutSettings.refundDaysMin}–${earlyCheckoutSettings.refundDaysMax} days` : '10–14 days');
        setMessage({ type: 'success', text: (language === 'ar' ? 'تم تطبيق الخروج المبكر. تاريخ المغادرة محدّث؛ الرسم من الأيام الملغاة؛ الاسترداد خلال ' : 'Early checkout applied. Check-out date updated; fee from cancelled days; refund within ') + days + (language === 'ar' ? ' يوماً عمل.' : ' business days.') });
        setEarlyCheckoutDate('');
        setEarlyCheckoutAvailable(null);
        setConfirmationData(null);
      })
      .catch((err) => {
        setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'فشل إرسال الطلب' : 'Failed to submit request') });
      })
      .finally(() => setEarlyCheckoutSubmitting(false));
  };

  // Derive late checkout hours from time picker (12:00 = 0, 13:00 = 1, etc.)
  const lateCheckoutHoursFromTime = (() => {
    if (!lateCheckoutTime) return 0;
    const [h, m] = lateCheckoutTime.split(':').map(Number);
    if (h < 12) return 0;
    return (h - 12) + (m || 0) / 60;
  })();

  // Calculate late checkout fee client-side from super admin settings
  const lateCheckoutFeeAmount = (() => {
    if (lateCheckoutHoursFromTime <= 0 || !reservation?.pricing) return 0;
    const lateSettings = earlyCheckoutSettings?.lateCheckout || { feeType: 'percentage', feeValue: 10 };
    const pricePerNight = reservation.pricing.numberOfNights > 0
      ? reservation.pricing.subtotal / reservation.pricing.numberOfNights
      : 0;
    const hourlyRate = pricePerNight / 24;
    const baseAmount = lateCheckoutHoursFromTime * hourlyRate;
    if (lateSettings.feeType === 'percentage') {
      return (baseAmount * Math.min(100, Math.max(0, lateSettings.feeValue || 0))) / 100;
    }
    return lateCheckoutHoursFromTime * Math.max(0, lateSettings.feeValue || 0);
  })();

  const submitLateCheckout = () => {
    if (lateCheckoutHoursFromTime <= 0) {
      // Remove late checkout - no modal needed
      setLateCheckoutSubmitting(true);
      setMessage({ type: '', text: '' });
      api.patch(`/api/bookings/${id}/request-late-checkout`, { lateCheckOutHours: 0 })
        .then((res) => {
          setReservation(res.data?.reservation || reservation);
          setMessage({ type: 'success', text: language === 'ar' ? 'تم تحديث وقت الخروج المتأخر' : 'Late checkout has been updated.' });
        })
        .catch((err) => {
          setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'فشل تحديث الخروج المتأخر' : 'Failed to update late checkout') });
        })
        .finally(() => setLateCheckoutSubmitting(false));
      return;
    }
    setLateCheckoutSubmitting(true);
    setMessage({ type: '', text: '' });
    setLateCheckoutAvailabilityError(null);
    api.get(`/api/bookings/${id}/late-checkout-availability`)
      .then((availRes) => {
        if (!availRes.data?.isAvailable) {
          setLateCheckoutAvailabilityError(availRes.data?.message || (language === 'ar'
            ? 'العقار لديه حجز آخر يدخل في يوم مغادرتك. الخروج المتأخر غير متاح.'
            : 'This property has another reservation checking in on your checkout day. Late checkout is not available.'));
          setLateCheckoutSubmitting(false);
          return;
        }
        setLateCheckoutConfirmData({
          requestedCheckOutTime: lateCheckoutTime,
          lateCheckOutHours: lateCheckoutHoursFromTime,
          feeAmount: lateCheckoutFeeAmount
        });
        setShowLateCheckoutConfirmModal(true);
        setLateCheckoutSubmitting(false);
      })
      .catch((err) => {
        setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'تعذر التحقق من التوفر' : 'Could not check availability') });
        setLateCheckoutSubmitting(false);
      });
  };

  const confirmLateCheckout = () => {
    if (!lateCheckoutConfirmData) return;
    setLateCheckoutSubmitting(true);
    setShowLateCheckoutConfirmModal(false);
    setMessage({ type: '', text: '' });
    api.patch(`/api/bookings/${id}/request-late-checkout`, {
      requestedCheckOutTime: lateCheckoutConfirmData.requestedCheckOutTime,
      lateCheckOutHours: lateCheckoutConfirmData.lateCheckOutHours
    })
      .then((res) => {
        setReservation(res.data?.reservation || reservation);
        setMessage({ type: 'success', text: language === 'ar' ? 'تم تحديث وقت الخروج المتأخر' : 'Late checkout has been updated.' });
        setLateCheckoutConfirmData(null);
      })
      .catch((err) => {
        if (err.response?.status === 409) {
          setLateCheckoutAvailabilityError(err.response?.data?.message || (language === 'ar'
            ? 'العقار لديه حجز آخر يدخل في يوم مغادرتك. الخروج المتأخر غير متاح.'
            : 'This property has another reservation checking in on your checkout day. Late checkout is not available.'));
        } else {
          setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'فشل تحديث الخروج المتأخر' : 'Failed to update late checkout') });
        }
        setLateCheckoutConfirmData(null);
      })
      .finally(() => setLateCheckoutSubmitting(false));
  };

  const checkAddDaysAvailability = () => {
    if (!addDaysDate) return;
    setAddDaysChecking(true);
    setAddDaysAvailable(null);
    api.get(`/api/bookings/${id}/add-days-availability`, { params: { newCheckOutDate: addDaysDate } })
      .then((res) => {
        setAddDaysAvailable(res.data?.isAvailable ?? false);
        setMessage({ type: res.data?.isAvailable ? 'success' : 'error', text: res.data?.message || '' });
      })
      .catch((err) => {
        setAddDaysAvailable(false);
        setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'تعذر التحقق' : 'Could not check availability') });
      })
      .finally(() => setAddDaysChecking(false));
  };

  // Calculate cancellation fee and refund from policy
  const getCancellationFeeBreakdown = () => {
    const totalAmount = reservation?.pricing?.totalAmount ?? 0;
    const policy = earlyCheckoutSettings?.cancellationPolicy || earlyCheckoutSettings;
    if (!policy) return { fee: 0, refund: totalAmount };
    const feeType = policy.feeType || 'percentage';
    const feeValue = Number(policy.feeValue) || 0;
    let fee = 0;
    if (feeType === 'percentage') {
      fee = (totalAmount * Math.min(100, Math.max(0, feeValue))) / 100;
    } else {
      fee = Math.min(totalAmount, Math.max(0, feeValue));
    }
    return { fee, refund: Math.max(0, totalAmount - fee) };
  };

  const openCancelModal = () => {
    setCancelReason('');
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    setCancelSubmitting(true);
    setMessage({ type: '', text: '' });
    api.patch(`/api/bookings/${id}/cancel`, { reason: cancelReason?.trim() || '' })
      .then((res) => {
        setReservation(res.data?.reservation || { ...reservation, status: 'Cancelled', cancellation: res.data?.reservation?.cancellation });
        setShowCancelModal(false);
        setMessage({ type: 'success', text: language === 'ar' ? 'تم إلغاء الحجز بنجاح. سيتم استرداد المبلغ خلال 10–14 يوماً عمل.' : 'Reservation cancelled successfully. Refund will be processed within 10–14 business days.' });
        navigate(`/client/booking/${id}`, { replace: true });
      })
      .catch((err) => {
        setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'فشل إلغاء الحجز' : 'Failed to cancel reservation') });
      })
      .finally(() => setCancelSubmitting(false));
  };

  const submitAddDays = () => {
    if (!addDaysDate || addDaysAvailable !== true) return;
    setAddDaysSubmitting(true);
    setMessage({ type: '', text: '' });
    api.patch(`/api/bookings/${id}/request-add-days`, { newCheckOutDate: addDaysDate })
      .then((res) => {
        setReservation(res.data?.reservation || reservation);
        setMessage({ type: 'success', text: language === 'ar' ? 'تمت إضافة الأيام. تم إرسال بريد إلكتروني لك وللمالك.' : 'Additional days have been added. Emails have been sent to you and the property owner.' });
        setAddDaysDate('');
        setAddDaysAvailable(null);
      })
      .catch((err) => {
        setMessage({ type: 'error', text: err.response?.data?.message || (language === 'ar' ? 'فشل إضافة الأيام' : 'Failed to add days') });
      })
      .finally(() => setAddDaysSubmitting(false));
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#D5B577' }} />
            <p className="text-gray-400">{language === 'ar' ? 'جاري تحميل تفاصيل الإقامة...' : 'Loading stay details...'}</p>
          </div>
        </div>
        <WhatsAppButton phoneNumber="+1234567890" message="Hello! I have a question about my stay." />
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="pt-32 pb-12 px-4 max-w-2xl mx-auto">
          <div className="rounded-2xl p-6 border bg-slate-800/50" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
            <p className="text-red-300 mb-4">{error || (language === 'ar' ? 'الحجز غير موجود' : 'Booking not found')}</p>
            <button
              onClick={() => navigate('/client')}
              className="px-4 py-2 rounded-xl font-medium transition-colors"
              style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
            >
              {language === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to dashboard'}
            </button>
          </div>
        </div>
        <WhatsAppButton phoneNumber="+1234567890" message="Hello! I have a question about my stay." />
      </div>
    );
  }

  const prop = reservation.property || {};
  const pricing = reservation.pricing || {};
  const showCriteria = (reservation.status === 'CheckedIn' || reservation.status === 'CheckedOut') && Array.isArray(reservation.criteriaWithPrices) && reservation.criteriaWithPrices.length > 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
      <Navbar />
      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <button
          onClick={() => navigate(`/client/booking/${id}`)}
          className="flex items-center gap-2 text-gray-300 hover:text-[#D5B577] transition-colors mb-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m7 7h18" />
          </svg>
          {language === 'ar' ? 'العودة إلى تفاصيل الحجز' : 'Back to booking details'}
        </button>

        <div className="rounded-2xl overflow-hidden border mb-6" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
          <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm font-mono mb-1" style={{ color: '#D5B577' }}>{reservation.reservationNumber}</p>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">
                  {language === 'ar' ? 'تفاصيل الإقامة والتعديلات' : 'Stay details & modifications'}
                </h1>
                <p className="text-gray-400 mt-1">
                  {typeof prop.title === 'string' ? prop.title : (prop.title?.en || prop.title?.ar || 'Property')}
                </p>
              </div>
              {canCancelReservation && (
                <button
                  type="button"
                  onClick={openCancelModal}
                  className="px-4 py-2 rounded-xl font-medium transition-colors border flex items-center gap-2"
                  style={{ borderColor: 'rgba(239, 68, 68, 0.6)', color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {language === 'ar' ? 'إلغاء الحجز' : 'Cancel reservation'}
                </button>
              )}
            </div>
          </div>

          {message.text && (
            <div
              className={`mx-6 mt-6 p-4 rounded-xl border ${message.type === 'success' ? 'bg-green-900/20 border-green-500/40 text-green-200' : 'bg-red-900/20 border-red-500/40 text-red-200'}`}
            >
              {message.text}
            </div>
          )}

          {/* Price Summary */}
          <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {language === 'ar' ? 'ملخص السعر' : 'Price Summary'}
            </h2>
            
            <div className="bg-slate-700/30 rounded-2xl p-5 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
              <div className="space-y-3">
                {/* Nights × Price */}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">
                    {pricing.numberOfNights || 0} {language === 'ar' ? 'ليلة' : 'night(s)'} × {formatPrice(pricing.pricePerNight || 0)}
                  </span>
                  <span className="text-white font-medium">{formatPrice(pricing.subtotal || 0)}</span>
                </div>
                
                {/* Insurance */}
                {(pricing.insurancePrice > 0) && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'وديعة التأمين' : 'Insurance deposit'}</span>
                    <span className="text-white font-medium">{formatPrice(pricing.insurancePrice)}</span>
                  </div>
                )}
                
                {/* Early check-in fee */}
                {(pricing.earlyCheckInFee > 0) && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'رسوم الدخول المبكر' : 'Early check-in fee'}</span>
                    <span className="text-white font-medium">{formatPrice(pricing.earlyCheckInFee)}</span>
                  </div>
                )}
                
                {/* Late checkout fee */}
                {(pricing.lateCheckOutFee > 0) && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'رسوم الخروج المتأخر' : 'Late checkout fee'}</span>
                    <span className="text-white font-medium">{formatPrice(pricing.lateCheckOutFee)}</span>
                  </div>
                )}
                
                {/* Discount */}
                {(pricing.discountAmount > 0) && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-green-400">{language === 'ar' ? 'الخصم' : 'Discount'}</span>
                    <span className="text-green-400 font-medium">- {formatPrice(pricing.discountAmount)}</span>
                  </div>
                )}
                
                {/* Early checkout info */}
                {reservation.earlyCheckout?.type === 'byDays' && reservation.earlyCheckout?.status === 'completed' && (
                  <>
                    <div className="border-t pt-3 mt-3" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-amber-400 text-sm font-medium">{language === 'ar' ? 'تم تطبيق الخروج المبكر' : 'Early checkout applied'}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-red-300">{language === 'ar' ? 'رسم الخروج المبكر' : 'Early checkout fee'}</span>
                        <span className="text-red-400 font-medium">- {formatPrice(reservation.earlyCheckout.feeAmount || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-green-300">{language === 'ar' ? 'مبلغ الاسترداد' : 'Refund amount'}</span>
                        <span className="text-green-400 font-medium">+ {formatPrice(reservation.earlyCheckout.refundAmount || 0)}</span>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Total */}
                <div className="border-t pt-3 mt-3 flex justify-between items-center" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                  <span className="text-white font-semibold">{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                  <span className="text-xl font-bold" style={{ color: '#D5B577' }}>{formatPrice(pricing.totalAmount || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Criteria & Insurance */}
          {showCriteria && (
            <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {language === 'ar' ? 'المعايير ووديعة التأمين' : 'Criteria & insurance'}
              </h2>
              <p className="text-gray-400 text-sm mb-4 max-w-2xl">
                {language === 'ar'
                  ? 'هذه هي المعايير التي تم التحقق منها عند دخولك. في حال وجود أي عنصر تالف أو مفقود عند المغادرة، سيتم خصم سعره من وديعة التأمين الخاصة بك.'
                  : 'These are the criteria verified at check-in. If any item is damaged or missing at check-out, its price will be deducted from your insurance deposit.'}
              </p>
              <div className="rounded-xl border overflow-hidden mb-4" style={{ borderColor: 'rgba(213, 181, 119, 0.3)', backgroundColor: 'rgba(15, 23, 42, 0.6)' }}>
                <table className="w-full text-left">
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(213, 181, 119, 0.12)' }}>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400" style={{ color: '#D5B577' }}>
                        {language === 'ar' ? 'العنصر' : 'Item'}
                      </th>
                      {reservation.criteriaWithPrices.some((c) => c.quantifiable) && (
                        <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400" style={{ color: '#D5B577' }}>
                          {language === 'ar' ? 'الكمية' : 'Qty'}
                        </th>
                      )}
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-right" style={{ color: '#D5B577' }}>
                        {language === 'ar' ? 'السعر (خصم عند التلف/الفقدان)' : 'Price (deducted if damaged/missing)'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservation.criteriaWithPrices.map((c, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-gray-700/50 hover:bg-white/5 transition-colors"
                        style={{ borderColor: 'rgba(213, 181, 119, 0.15)' }}
                      >
                        <td className="px-4 py-3 text-white font-medium">
                          {language === 'ar' ? (c.nameAr || c.name) : (c.name || c.nameAr)}
                        </td>
                        {reservation.criteriaWithPrices.some((cr) => cr.quantifiable) && (
                          <td className="px-4 py-3 text-gray-300">{c.quantifiable ? (c.quantity ?? 1) : '—'}</td>
                        )}
                        <td className="px-4 py-3 text-right font-medium" style={{ color: '#D5B577' }}>
                          {c.clientPrice > 0 ? formatPrice(c.clientPrice) : (language === 'ar' ? '—' : '—')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(213, 181, 119, 0.08)', border: '1px solid rgba(213, 181, 119, 0.25)' }}>
                <span className="text-gray-400 text-sm">{language === 'ar' ? 'إجمالي وديعة التأمين' : 'Total insurance deposit'}:</span>
                <span className="text-lg font-bold" style={{ color: '#D5B577' }}>
                  {formatPrice(pricing.insurancePrice ?? prop.insurancePrice ?? 0)} {pricing.currency || 'EGP'}
                </span>
                <p className="text-gray-500 text-xs max-w-xl">
                  {language === 'ar'
                    ? 'يتم استرداد المبلغ المتبقي بعد خصم أي عناصر تالفة أو مفقودة خلال 10–14 يوماً عمل بعد المغادرة.'
                    : 'Any remaining amount after deducting damaged or missing items will be refunded within 10–14 business days after check-out.'}
                </p>
              </div>
            </div>
          )}

          {/* Insurance deduction & damage/missing items (CheckedOut only) */}
          {reservation.status === 'CheckedOut' && (
            <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {language === 'ar' ? 'وديعة التأمين والخصومات' : 'Insurance deposit & deductions'}
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                {language === 'ar'
                  ? 'تفاصيل وديعة التأمين وأي خصومات تمت بسبب عناصر تالفة أو مفقودة.'
                  : 'Details of your insurance deposit and any deductions for damaged or missing items.'}
              </p>

              {/* Damaged items */}
              {reservation.checkOut?.damagedItems && reservation.checkOut.damagedItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-md font-semibold text-white mb-2">{language === 'ar' ? 'عناصر تالفة' : 'Damaged items'}</h3>
                  <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(213, 181, 119, 0.3)', backgroundColor: 'rgba(15, 23, 42, 0.6)' }}>
                    <table className="w-full text-left">
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(213, 181, 119, 0.12)' }}>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#D5B577' }}>
                            {language === 'ar' ? 'العنصر' : 'Item'}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#D5B577' }}>
                            {language === 'ar' ? 'الوصف' : 'Description'}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-right" style={{ color: '#D5B577' }}>
                            {language === 'ar' ? 'التكلفة' : 'Cost'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {reservation.checkOut.damagedItems.map((item, idx) => (
                          <tr key={idx} className="border-t border-gray-700/50" style={{ borderColor: 'rgba(213, 181, 119, 0.15)' }}>
                            <td className="px-4 py-3 text-white font-medium">
                              {language === 'ar' ? (item.nameAr || item.name) : (item.name || item.nameAr)}
                            </td>
                            <td className="px-4 py-3 text-gray-300">{item.description || '—'}</td>
                            <td className="px-4 py-3 text-right font-medium" style={{ color: '#D5B577' }}>
                              {item.cost != null ? formatPrice(item.cost) : (language === 'ar' ? 'قيد التقدير' : 'Pending estimate')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Missing items */}
              {reservation.checkOut?.missingItems && reservation.checkOut.missingItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-md font-semibold text-white mb-2">{language === 'ar' ? 'عناصر مفقودة' : 'Missing items'}</h3>
                  <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(213, 181, 119, 0.3)', backgroundColor: 'rgba(15, 23, 42, 0.6)' }}>
                    <table className="w-full text-left">
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(213, 181, 119, 0.12)' }}>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#D5B577' }}>
                            {language === 'ar' ? 'العنصر' : 'Item'}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#D5B577' }}>
                            {language === 'ar' ? 'الكمية' : 'Quantity'}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-right" style={{ color: '#D5B577' }}>
                            {language === 'ar' ? 'السعر' : 'Price'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {reservation.checkOut.missingItems.map((item, idx) => (
                          <tr key={idx} className="border-t border-gray-700/50" style={{ borderColor: 'rgba(213, 181, 119, 0.15)' }}>
                            <td className="px-4 py-3 text-white font-medium">
                              {language === 'ar' ? (item.nameAr || item.name) : (item.name || item.nameAr)}
                            </td>
                            <td className="px-4 py-3 text-gray-300">{item.quantity ?? 1}</td>
                            <td className="px-4 py-3 text-right font-medium" style={{ color: '#D5B577' }}>
                              {formatPrice(item.clientPrice ?? 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Insurance summary */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(213, 181, 119, 0.08)', border: '1px solid rgba(213, 181, 119, 0.25)' }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm">{language === 'ar' ? 'وديعة التأمين' : 'Insurance deposit'}:</span>
                  <span className="font-semibold" style={{ color: '#D5B577' }}>
                    {formatPrice(pricing.insurancePrice ?? prop.insurancePrice ?? 0)} {pricing.currency || 'EGP'}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 text-sm">{language === 'ar' ? 'إجمالي الخصومات' : 'Total deductions'}:</span>
                  <span className="font-semibold text-red-300">
                    {formatPrice((reservation.checkOut?.insuranceDeduction ?? 0) + (reservation.checkOut?.damagedItemsDeduction ?? 0))} {pricing.currency || 'EGP'}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                  <span className="text-white font-semibold">{language === 'ar' ? 'المبلغ المسترد' : 'Amount returned'}:</span>
                  <span className="text-lg font-bold text-green-400">
                    {formatPrice(Math.max(0, (pricing.insurancePrice ?? prop.insurancePrice ?? 0) - ((reservation.checkOut?.insuranceDeduction ?? 0) + (reservation.checkOut?.damagedItemsDeduction ?? 0))))} {pricing.currency || 'EGP'}
                  </span>
                </div>
                {((reservation.checkOut?.insuranceDeduction ?? 0) + (reservation.checkOut?.damagedItemsDeduction ?? 0)) === 0 && (
                  <p className="text-green-400 text-xs mt-2">
                    {language === 'ar' ? 'لم يتم خصم أي مبلغ. سيتم استرداد وديعة التأمين بالكامل.' : 'No deductions. Full insurance deposit will be refunded.'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Early checkout: same-day (time only, no fee) or by-days (fee + refund, approval) */}
          {canModifyStay && currentCheckOut && (
            <div className="p-6 sm:p-8 border-b" style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}>
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {language === 'ar' ? 'الخروج المبكر' : 'Early checkout'}
              </h2>
              {reservation.earlyCheckout?.type === 'sameDay' && reservation.earlyCheckout?.status === 'completed' && (
                <p className="text-green-400 text-sm mb-4">
                  {language === 'ar' ? 'تم تسجيل الخروج المبكر في نفس اليوم (لا رسم).' : 'Same-day early checkout recorded. No fee applies.'}
                  {reservation.earlyCheckout?.requestedCheckOutTime && (
                    <span className="block mt-1">
                      {language === 'ar' ? 'وقت الخروج المطلوب: ' : 'Requested checkout time: '}
                      {new Date(reservation.earlyCheckout.requestedCheckOutTime).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                  )}
                </p>
              )}
              {reservation.earlyCheckout?.type === 'byDays' && reservation.earlyCheckout?.status === 'rejected' && (
                <p className="text-red-300 text-sm mb-4">
                  {language === 'ar' ? 'تم رفض طلب الخروج المبكر (بأيام).' : 'Your early checkout by days request was not approved.'}
                  {reservation.earlyCheckout?.rejectionReason && (
                    <span className="block mt-1">{reservation.earlyCheckout.rejectionReason}</span>
                  )}
                </p>
              )}
              {reservation.earlyCheckout?.type === 'byDays' && (reservation.earlyCheckout?.status === 'approved' || reservation.earlyCheckout?.status === 'completed') && (
                <p className="text-green-400 text-sm mb-4">
                  {language === 'ar' ? 'تم تطبيق الخروج المبكر. تاريخ الخروج الجديد محدّث؛ الرسم محسوب من الأيام الملغاة؛ الاسترداد خلال الفترة المحددة.' : 'Early checkout by days applied. New check-out date updated; fee applied from cancelled days; refund will be processed within the configured timeframe.'}
                  {reservation.earlyCheckout?.refundAmount != null && (
                    <span className="block mt-1">
                      {language === 'ar' ? 'مبلغ الاسترداد: ' : 'Refund amount: '}
                      {formatPrice(reservation.earlyCheckout.refundAmount)} EGP
                    </span>
                  )}
                </p>
              )}
              {/* Show form when no completed/approved early checkout (show for: none, rejected, pending, or empty object) */}
              {!(reservation.earlyCheckout?.type && (reservation.earlyCheckout?.status === 'completed' || reservation.earlyCheckout?.status === 'approved')) && (
                <>
                  <p className="text-gray-400 text-sm mb-4">
                    {language === 'ar'
                      ? 'اختر الخروج المبكر في نفس اليوم (وقت أبكر فقط) أو خروج قبل يوم أو أكثر (رسم من الأيام الملغاة + استرداد، يُطبّق فوراً).'
                      : 'Choose same-day early checkout (earlier time only, no fee) or checkout 1+ days earlier (fee from cancelled days + refund, applied immediately).'}
                  </p>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="earlyType"
                        checked={earlyCheckoutType === 'sameDay'}
                        onChange={() => { setEarlyCheckoutType('sameDay'); setEarlyCheckoutAvailable(null); setMessage({ type: '', text: '' }); }}
                        className="rounded"
                      />
                      <span className="text-white text-sm">
                        {language === 'ar' ? 'نفس اليوم (وقت أبكر فقط، بدون رسم)' : 'Same day (earlier time only, no fee)'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="earlyType"
                        checked={earlyCheckoutType === 'byDays'}
                        onChange={() => { setEarlyCheckoutType('byDays'); setEarlyCheckoutTime(''); setMessage({ type: '', text: '' }); }}
                        className="rounded"
                      />
                      <span className="text-white text-sm">
                        {language === 'ar' ? 'خروج قبل يوم أو أكثر (رسم من الأيام الملغاة + استرداد، يُطبّق فوراً)' : 'By days (1+ days earlier: fee from cancelled days + refund, applied immediately)'}
                      </span>
                    </label>
                  </div>
                  {earlyCheckoutType === 'sameDay' && (
                    <div className="flex flex-wrap items-end gap-3">
                      <p className="text-gray-400 text-sm mb-2 w-full">
                        {language === 'ar'
                          ? 'الخروج المعياري في يوم المغادرة الساعة 12:00 ظهراً. اختر وقتاً (مثلاً 8 صباحاً) ليصبح خروجك في يوم المغادرة في هذا الوقت وليس 12 ظهراً.'
                          : 'Normal checkout on your checkout day is 12:00 PM. Choose a time (e.g. 8:00 AM) and on your checkout date you will check out at that time instead of 12 PM.'}
                      </p>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{language === 'ar' ? 'وقت الخروج المطلوب (قبل 12:00 ظهراً)' : 'Requested checkout time (before 12:00 PM)'}</label>
                        <input
                          type="time"
                          value={earlyCheckoutTime}
                          onChange={(e) => setEarlyCheckoutTime(e.target.value)}
                          max="11:59"
                          className="h-11 px-4 rounded-xl border bg-slate-800 text-white"
                          style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={submitEarlyCheckout}
                        disabled={!earlyCheckoutTime || earlyCheckoutSubmitting}
                        className="h-11 px-4 rounded-xl font-medium transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                      >
                        {earlyCheckoutSubmitting ? (language === 'ar' ? 'جاري التسجيل...' : 'Submitting...') : (language === 'ar' ? 'تسجيل الخروج المبكر (نفس اليوم)' : 'Submit same-day early checkout')}
                      </button>
                    </div>
                  )}
                  {earlyCheckoutType === 'byDays' && (
                    <>
                      <p className="text-gray-400 text-sm mb-4">
                        {language === 'ar'
                          ? 'اختر تاريخ خروج أبكر. الرسم يُحسب من الأيام الملغاة فقط (مثلاً 10٪ من قيمة الأيام المتبقية). سيظهر ملخص بالمبلغ المخصوم والمسترد قبل التأكيد. يُسترد المبلغ خلال ' + (earlyCheckoutSettings ? `${earlyCheckoutSettings.refundDaysMin}–${earlyCheckoutSettings.refundDaysMax}` : '10–14') + ' يوماً عمل.'
                          : 'Choose an earlier check-out date. Fee is calculated from cancelled days only (e.g. 10% of the remaining days value). You will see a summary of deducted and refunded amounts before confirming. Refund within ' + (earlyCheckoutSettings ? `${earlyCheckoutSettings.refundDaysMin}–${earlyCheckoutSettings.refundDaysMax}` : '10–14') + ' business days.'}
                      </p>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">{language === 'ar' ? 'تاريخ الخروج الجديد' : 'New check-out date'}</label>
                          <input
                            type="date"
                            value={earlyCheckoutDate}
                            onChange={(e) => { setEarlyCheckoutDate(e.target.value); setMessage({ type: '', text: '' }); }}
                            min={getEarlyCheckoutMinDate()}
                            max={getEarlyCheckoutMaxDate()}
                            className="h-11 px-4 rounded-xl border bg-slate-800 text-white"
                            style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={submitEarlyCheckout}
                          disabled={!earlyCheckoutDate || earlyCheckoutSubmitting}
                          className="h-11 px-4 rounded-xl font-medium transition-colors disabled:opacity-50"
                          style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                        >
                          {earlyCheckoutSubmitting ? (language === 'ar' ? 'جاري التطبيق...' : 'Applying...') : (language === 'ar' ? 'تطبيق الخروج المبكر' : 'Apply early checkout')}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Late checkout - disabled when early checkout is chosen */}
          {canModifyStay && (
            <div
              className={`p-6 sm:p-8 border-b relative ${hasEarlyCheckout ? 'opacity-60 pointer-events-none' : ''}`}
              style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(213, 181, 119, 0.2)' }}
            >
              {hasEarlyCheckout && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 rounded-lg z-10">
                  <p className="text-amber-300 text-sm font-medium px-4 text-center">
                    {language === 'ar' ? 'الخروج المتأخر غير متاح عند اختيار الخروج المبكر' : 'Late checkout is not available when early checkout is selected.'}
                  </p>
                </div>
              )}
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {language === 'ar' ? 'الخروج المتأخر' : 'Late checkout'}
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                {language === 'ar'
                  ? 'اختر وقت المغادرة المتأخر (بعد 12:00 ظهراً). الرسم يُحسب تلقائياً حسب النسبة المحددة من الإدارة. الخروج المتأخر غير متاح إذا كان هناك حجز آخر يدخل في يوم مغادرتك.'
                  : 'Choose a later check-out time (after 12:00 PM). Fee is calculated automatically based on the percentage set by admin. Late checkout is not available if another reservation checks in on your checkout day.'}
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{language === 'ar' ? 'وقت المغادرة' : 'Check-out time'}</label>
                  <input
                    type="time"
                    value={lateCheckoutTime}
                    onChange={(e) => { setLateCheckoutTime(e.target.value); setLateCheckoutAvailabilityError(null); }}
                    min="12:00"
                    max="23:59"
                    disabled={hasEarlyCheckout}
                    className="h-11 px-4 rounded-xl border bg-slate-800 text-white disabled:opacity-50"
                    style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
                  />
                </div>
                {lateCheckoutHoursFromTime > 0 && lateCheckoutFeeAmount > 0 && (
                  <div className="text-sm" style={{ color: '#D5B577' }}>
                    {language === 'ar' ? 'الرسم المتوقع: ' : 'Estimated fee: '}{formatPrice(lateCheckoutFeeAmount)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={submitLateCheckout}
                  disabled={hasEarlyCheckout || lateCheckoutSubmitting || (lateCheckoutHoursFromTime <= 0 && !(reservation?.lateCheckOutHours > 0))}
                  className="h-11 px-4 rounded-xl font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                >
                  {lateCheckoutSubmitting ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ الخروج المتأخر' : 'Save late checkout')}
                </button>
              </div>

              {/* Late checkout availability error modal */}
              {lateCheckoutAvailabilityError && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
                  onClick={() => setLateCheckoutAvailabilityError(null)}
                >
                  <div
                    className="mx-4 max-w-md rounded-xl p-6 shadow-xl"
                    style={{ backgroundColor: '#1D2A3B', border: '1px solid rgba(239, 68, 68, 0.5)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="mb-4 flex items-center gap-2 text-red-400">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-lg font-bold">
                        {language === 'ar' ? 'الخروج المتأخر غير متاح' : 'Late checkout not available'}
                      </h3>
                    </div>
                    <p className="text-gray-300 mb-6">{lateCheckoutAvailabilityError}</p>
                    <button
                      type="button"
                      onClick={() => setLateCheckoutAvailabilityError(null)}
                      className="w-full h-11 rounded-xl font-medium"
                      style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                    >
                      {language === 'ar' ? 'حسناً' : 'OK'}
                    </button>
                  </div>
                </div>
              )}

              {/* Late checkout confirmation modal */}
              {showLateCheckoutConfirmModal && lateCheckoutConfirmData && (
                <div
                  className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                  onClick={() => { setShowLateCheckoutConfirmModal(false); setLateCheckoutConfirmData(null); }}
                >
                  <div
                    className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 max-w-lg w-full border-2 shadow-2xl transform transition-all"
                    style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}>
                          <svg className="w-7 h-7" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">
                            {language === 'ar' ? 'تأكيد الخروج المتأخر' : 'Confirm Late Checkout'}
                          </h3>
                          <p className="text-gray-400 text-sm mt-1">
                            {language === 'ar' ? 'مراجعة التفاصيل' : 'Review details'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => { setShowLateCheckoutConfirmModal(false); setLateCheckoutConfirmData(null); }}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-4 mb-6">
                      <div className="bg-slate-700/30 rounded-2xl p-4 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">{language === 'ar' ? 'وقت المغادرة الجديد' : 'New check-out time'}:</span>
                            <span className="text-white font-semibold">
                              {(() => {
                                const [h, m] = (lateCheckoutConfirmData?.requestedCheckOutTime || '12:00').split(':').map(Number);
                                const d = new Date(2000, 0, 1, h, m || 0);
                                return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hour12: true });
                              })()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">{language === 'ar' ? 'رسوم الخروج المتأخر' : 'Late checkout fee'}:</span>
                            <span className="text-amber-300 font-bold">+ {formatPrice(lateCheckoutConfirmData?.feeAmount || 0)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {language === 'ar'
                          ? 'سيتم إضافة هذا المبلغ إلى إجمالي حجزك.'
                          : 'This amount will be added to your total reservation.'}
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => { setShowLateCheckoutConfirmModal(false); setLateCheckoutConfirmData(null); }}
                        className="flex-1 h-12 rounded-xl font-medium border transition-colors"
                        style={{ borderColor: 'rgba(213, 181, 119, 0.5)', color: '#D5B577' }}
                      >
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                      </button>
                      <button
                        type="button"
                        onClick={confirmLateCheckout}
                        disabled={lateCheckoutSubmitting}
                        className="flex-1 h-12 rounded-xl font-medium transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                      >
                        {lateCheckoutSubmitting ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'تأكيد' : 'Confirm')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Add days */}
          {canModifyStay && currentCheckOut && (
            <div className="p-6 sm:p-8" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
              <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {language === 'ar' ? 'إضافة أيام' : 'Add days'}
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                {language === 'ar'
                  ? 'اختر تاريخ خروج جديد لتمديد إقامتك. يمكنك الحجز فقط إذا لم تكن التواريخ الإضافية محجوزة. سيتم إرسال بريد إلكتروني لك وللمالك.'
                  : 'Choose a new check-out date to extend your stay. You can only book if the additional dates are not already booked. An email will be sent to you and the property owner.'}
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{language === 'ar' ? 'تاريخ الخروج الجديد' : 'New check-out date'}</label>
                  <input
                    type="date"
                    value={addDaysDate}
                    onChange={(e) => { setAddDaysDate(e.target.value); setAddDaysAvailable(null); }}
                    min={(() => {
                      if (!reservation?.checkOutDate) return undefined;
                      const checkOutLocal = parseToLocalDate(reservation.checkOutDate);
                      if (!checkOutLocal) return undefined;
                      const dayAfterCheckout = new Date(checkOutLocal);
                      dayAfterCheckout.setDate(dayAfterCheckout.getDate() + 1);
                      return formatDateYMD(dayAfterCheckout);
                    })()}
                    className="h-11 px-4 rounded-xl border bg-slate-800 text-white"
                    style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={checkAddDaysAvailability}
                  disabled={!addDaysDate || addDaysChecking}
                  className="h-11 px-4 rounded-xl font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.5)' }}
                >
                  {addDaysChecking ? (language === 'ar' ? 'جاري التحقق...' : 'Checking...') : (language === 'ar' ? 'تحقق من التوفر' : 'Check availability')}
                </button>
                {addDaysAvailable === true && (
                  <button
                    type="button"
                    onClick={submitAddDays}
                    disabled={addDaysSubmitting}
                    className="h-11 px-4 rounded-xl font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                  >
                    {addDaysSubmitting ? (language === 'ar' ? 'جاري الإضافة...' : 'Adding...') : (language === 'ar' ? 'إضافة الأيام' : 'Add days')}
                  </button>
                )}
              </div>
              {addDaysAvailable === false && (
                <p className="mt-3 text-amber-300 text-sm">
                  {language === 'ar' ? 'العقار محجوز في أحد التواريخ المطلوبة. يرجى اختيار تواريخ أخرى.' : 'This property is already booked on one or more of the requested dates. Please choose different dates.'}
                </p>
              )}
              {reservation.extendedStayRequestedAt && (
                <p className="mt-3 text-green-400 text-sm flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                  {language === 'ar' ? 'تم تمديد إقامتك مسبقاً.' : 'Your stay has been extended.'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <WhatsAppButton phoneNumber="+1234567890" message="Hello! I have a question about my stay." />
      
      {/* Confirmation Modal for Early Checkout by Days */}
      {showConfirmModal && confirmationData && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowConfirmModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 max-w-lg w-full border-2 shadow-2xl transform transition-all"
            style={{ borderColor: 'rgba(213, 181, 119, 0.4)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)' }}>
                  <svg className="w-7 h-7" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {language === 'ar' ? 'تأكيد الخروج المبكر' : 'Confirm Early Checkout'}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {language === 'ar' ? 'مراجعة التفاصيل المالية' : 'Review financial details'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4 mb-6">
              {/* Booking Summary */}
              <div className="bg-slate-700/30 rounded-2xl p-4 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-white">{language === 'ar' ? 'ملخص الحجز' : 'Booking Summary'}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'الأيام الملغاة' : 'Cancelled nights'}:</span>
                    <span className="text-white font-semibold">{confirmationData.cancelledNights} {language === 'ar' ? 'ليلة' : 'night(s)'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{language === 'ar' ? 'قيمة الأيام الملغاة' : 'Value of cancelled days'}:</span>
                    <span className="text-white font-semibold">{formatPrice(confirmationData.valueOfCancelledDays)}</span>
                  </div>
                </div>
              </div>

              {/* Calculation Breakdown */}
              <div className="bg-slate-700/30 rounded-2xl p-4 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-white">{language === 'ar' ? 'حساب الاسترداد' : 'Refund Calculation'}</span>
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-gray-400">{language === 'ar' ? 'المبلغ المدفوع للأيام الملغاة' : 'Amount paid for cancelled days'}:</span>
                    <span className="text-white font-semibold">{formatPrice(confirmationData.valueOfCancelledDays)}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center py-2 border-t border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.15)' }}>
                    <span className="text-red-300 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                      {language === 'ar' ? 'رسوم الخروج المبكر' : 'Early checkout fee'}
                    </span>
                    <span className="text-red-400 font-bold">- {formatPrice(confirmationData.feeAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center pt-1">
                    <span className="text-gray-400">{language === 'ar' ? 'صافي المبلغ المسترد' : 'Net refund amount'}:</span>
                    <span className="font-bold" style={{ color: '#D5B577' }}>{formatPrice(confirmationData.refundAmount)}</span>
                  </div>
                </div>
              </div>
              
              {/* Insurance Note */}
              {reservation?.pricing?.insurancePrice > 0 && (
                <div className="flex gap-3 p-4 rounded-xl bg-slate-700/30 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <p className="text-white text-sm font-medium mb-1">
                      {language === 'ar' ? 'ملاحظة عن التأمين' : 'Insurance Note'}
                    </p>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      {language === 'ar' 
                        ? `التأمين (${formatPrice(reservation.pricing.insurancePrice)}) سيتم استرداده بالكامل عند الخروج إذا لم تكن هناك أضرار.`
                        : `Insurance deposit (${formatPrice(reservation.pricing.insurancePrice)}) will be fully refunded at checkout if there are no damages.`}
                    </p>
                  </div>
                </div>
              )}

              {/* Refund Amount */}
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-2xl p-5 border-2 border-green-500/40 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-green-300 font-medium uppercase tracking-wide">{language === 'ar' ? 'المبلغ المسترد' : 'Total Refund'}</p>
                      <p className="text-3xl font-bold text-green-400 mt-1">{formatPrice(confirmationData.refundAmount)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Note */}
              <div className="flex gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-blue-200 text-sm leading-relaxed">
                  {language === 'ar' 
                    ? `سيتم معالجة الاسترداد خلال ${earlyCheckoutSettings?.refundDaysMin || 10}–${earlyCheckoutSettings?.refundDaysMax || 14} يوماً عمل إلى طريقة الدفع الأصلية.`
                    : `Refund will be processed within ${earlyCheckoutSettings?.refundDaysMin || 10}–${earlyCheckoutSettings?.refundDaysMax || 14} business days to your original payment method.`}
                </p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-6 py-3.5 rounded-xl font-semibold transition-all border-2 hover:bg-slate-700/50"
                style={{ borderColor: 'rgba(213, 181, 119, 0.5)', color: '#D5B577' }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={confirmEarlyCheckout}
                disabled={earlyCheckoutSubmitting}
                className="flex-1 px-6 py-3.5 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
              >
                {earlyCheckoutSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {language === 'ar' ? 'جاري التطبيق...' : 'Processing...'}
                  </span>
                ) : (
                  language === 'ar' ? 'تأكيد الخروج المبكر' : 'Confirm Early Checkout'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel reservation confirmation modal */}
      {showCancelModal && canCancelReservation && (() => {
        const { fee, refund } = getCancellationFeeBreakdown();
        const totalAmount = reservation?.pricing?.totalAmount ?? 0;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !cancelSubmitting && setShowCancelModal(false)}>
            <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border" style={{ backgroundColor: '#1e293b', borderColor: 'rgba(213, 181, 119, 0.3)' }} onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {language === 'ar' ? 'إلغاء الحجز' : 'Cancel reservation'}
                </h3>
                <p className="text-gray-400 text-sm mt-2">
                  {language === 'ar' ? 'سيتم تطبيق رسم إلغاء على حجزك. سيتم استرداد المبلغ خلال 10–14 يوماً عمل.' : 'A cancellation fee will be applied to your booking. The refund will be processed within 10–14 working days.'}
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {language === 'ar' ? 'سبب الإلغاء (اختياري)' : 'Reason for cancellation (optional)'}
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder={language === 'ar' ? 'أدخل سبب الإلغاء...' : 'Enter reason for cancellation...'}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border text-white placeholder-gray-500 focus:outline-none focus:ring-2"
                    style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}
                  />
                </div>
                <div className="p-4 rounded-xl bg-slate-700/30 border" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                  <p className="text-sm text-gray-300 mb-3">
                    {language === 'ar' ? 'سيتم استرداد المبلغ خلال 10–14 يوماً عمل.' : 'The refund will be processed within 10–14 working days.'}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">{language === 'ar' ? 'المبلغ المدفوع' : 'Amount paid'}</span>
                      <span className="text-white font-medium">{formatPrice(totalAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-t border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.15)' }}>
                      <span className="text-red-300">{language === 'ar' ? 'رسوم الإلغاء' : 'Cancellation fee'}</span>
                      <span className="text-red-400 font-bold">- {formatPrice(fee)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-gray-400">{language === 'ar' ? 'المبلغ المسترد' : 'Refund amount'}</span>
                      <span className="font-bold" style={{ color: '#D5B577' }}>{formatPrice(refund)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                <button
                  onClick={() => !cancelSubmitting && setShowCancelModal(false)}
                  disabled={cancelSubmitting}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all border-2"
                  style={{ borderColor: 'rgba(213, 181, 119, 0.5)', color: '#D5B577' }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Back'}
                </button>
                <button
                  onClick={confirmCancel}
                  disabled={cancelSubmitting}
                  className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                >
                  {cancelSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {language === 'ar' ? 'جاري الإلغاء...' : 'Cancelling...'}
                    </span>
                  ) : (
                    language === 'ar' ? 'تأكيد الإلغاء' : 'Confirm cancellation'
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default StayDetails;
